/*
 * Copyright (c) 2021 OTB Africa. All rights reserved. <https://otbafrica.com>
 *
 * Created Date: Sunday, September 26th 2021, 9:28:30 am
 * Author: OTB Africa, developers@otbafrica.com
 *
 * Use of this source code is governed by the license that can be found in
 * the LICENSE file or at https://developers.otbafrica.com/licenses/
 */

import "theme/css/ServiceBuilder.css";
import React from "react";
import { useParams } from "react-router";
import { useNavigate } from "react-router";
import { URLS } from "core/routes";
import { FullScreenDialog } from "ui-widgets";
import { Outlet } from "react-router-dom";
import Stack from "@mui/material/Stack";
import { FormBuilderProvider } from "form-builder/providers";
import { useNavigateToServiceFormBuilder } from "service-builder/routes/utils";
import {
  ServiceBuilderLeftPanel,
  ServiceBuilderNavBar,
  ServiceBuilderToolBarItems,
  ServiceBuilderSkeleton,
} from "service-builder/components/core";
import { NewTaskTemplateAddedNotification } from "service-builder/components/workflow-builder/NewTaskTemplateAddedNotification";
import { HorizontalStackWidget } from "ui-widgets";
import { useServiceBuilderContext } from "service-builder/providers";
import Box from "@mui/material/Box";
import { APIItemDisplayWrapper } from "ui-widgets/wrappers/APIRequestWrappers";
import { WorkflowProvider } from "service-builder/providers";

export const ServiceBuilderBaseLayout = () => {
  const { serviceId } = useParams();

  const [dialogOpen, setDialogOpen] = React.useState<boolean>(serviceId !== undefined);

  const navigate = useNavigate();

  const toggleDialogOpen = () => {
    setDialogOpen((prev) => !prev);
    navigate(URLS.services);
  };

  const { processDefinitionContext } = useServiceBuilderContext();

  const {
    currentProcessDefinition,
    isFetchingProcessDefinition,
    processDefinitionFetchHasError,
    fetchProcessDefinitionStatusCode,
  } = processDefinitionContext;

  return (
    <FullScreenDialog
      className="os-service-builder-full-screen"
      title="Service Builder"
      open={dialogOpen}
      handleClose={toggleDialogOpen}
      showAppBar={false}
    >
      <HorizontalStackWidget>
        <ServiceBuilderLeftPanel>
          <ServiceBuilderToolBarItems />
        </ServiceBuilderLeftPanel>

        <Stack className="os-service-builder-container">
          <APIItemDisplayWrapper
            showLoader={isFetchingProcessDefinition}
            loaderType="custom"
            customProgressLoader={<ServiceBuilderSkeleton />}
            isAPIError={processDefinitionFetchHasError}
            httpResponseStatusCode={fetchProcessDefinitionStatusCode}
          >
            {
              /* Only render content if processDefinition has been loaded. Outlet is also only rendered on the same condition
       so that our child components can always get access to the CurrentProcesDefinition */
              currentProcessDefinition?.id && (
                <>
                  <ServiceBuilderNavBar />

                  <Box className="os-service-builder-container-workspace">
                    <WorkflowProvider>
                      <FormBuilderProvider
                        useNavigateToFormBuilder={useNavigateToServiceFormBuilder}
                        portalIds={[currentProcessDefinition.site]}
                        formsAPIListFilter={{ process_definition: currentProcessDefinition.id }}
                        contextTagsAPIFilters={{ process_definition: currentProcessDefinition.id }}
                      >
                        <NewTaskTemplateAddedNotification />
                        <Outlet />
                      </FormBuilderProvider>
                    </WorkflowProvider>
                  </Box>
                </>
              )
            }
          </APIItemDisplayWrapper>
        </Stack>
      </HorizontalStackWidget>
    </FullScreenDialog>
  );
};
